import Config from '../../../config';
import {
    ActionItem,
    ActionablePriority,
    LongestStalled,
    OnboardingRow,
    PipelineCounts,
    ReasoningStep,
    Status,
} from '../onboardingIntelligence.types';
import { PartnerTasks, isAllTasksComplete } from '../partnerTasks';
import { ShippingLabelStatus, SHIPPING_LABEL_TASK_TITLE } from '../shippingLabelStatus';

const STALLED_DAYS_THRESHOLD = 60;
const PRIORITY_RANK: Record<ActionablePriority, number> = {
    Low: 0,
    Medium: 1,
    High: 2,
};

export const isStalled = (row: OnboardingRow): boolean =>
    row.Days >= STALLED_DAYS_THRESHOLD && row.Status !== 'Live';

export const actionableRow = (row: OnboardingRow): boolean => {
    if (row.Status === 'Onboarding') return true;
    if (row.Status === 'Go Live Requested') return true;
    if (row.Status === 'Form Received') return true;
    if (row.Status === 'Invite Sent') return false;
    if (row.Status === 'Live') return false;
    return row.Days >= STALLED_DAYS_THRESHOLD;
};

/**
 * Precedence (locked): S2 > S3 > S1.
 *   - S2: tasks all complete + Status ∈ {Onboarding, RequestedGoLive}
 *   - S3: Status === Form Received
 *   - S1: Days ≥ 60 && Status !== Live (fallback)
 */
export function classifyScenario(
    row: OnboardingRow,
    tasks: PartnerTasks | null
): ActionItem['kind'] | null {
    if (
        tasks &&
        isAllTasksComplete(tasks) &&
        (row.Status === 'Onboarding' || row.Status === 'Go Live Requested')
    ) {
        return 'scenario2';
    }
    if (row.Status === 'Form Received') return 'scenario3';
    if (row.Days >= STALLED_DAYS_THRESHOLD && row.Status !== 'Live') return 'scenario1';
    return null;
}

/**
 * Deterministic order matches RetailerOnboarding render order
 * (`supplier_onboarding.js:330–360`): Shipping label → Connection → Product → Inventory → Testing.
 * Returns a canned label when no tasks are available.
 */
export function pickIncompleteTask(tasks: PartnerTasks | null): string {
    if (!tasks) return 'Onboarding not started';
    if (tasks.shippingLabelEnabled && tasks.shippingLabelStatus !== ShippingLabelStatus.Complete)
        return SHIPPING_LABEL_TASK_TITLE;
    if (tasks.connectionPrimary && !tasks.connectionConfigured)
        return tasks.connectionPrimary;
    if (tasks.productEnabled && !tasks.productPosted)
        return tasks.productPoc ? 'Upload products' : 'Upload product feed';
    if (tasks.inventoryEnabled && !tasks.inventoryPosted) return 'Upload inventory';
    if (!Config.production && tasks.testingEnabled && !tasks.testingComplete)
        return 'Complete test cases';
    return '';
}

const compareStartDate = (a: OnboardingRow, b: OnboardingRow): number => {
    const aDate = a.StartDate ? Date.parse(a.StartDate) : Number.POSITIVE_INFINITY;
    const bDate = b.StartDate ? Date.parse(b.StartDate) : Number.POSITIVE_INFINITY;
    return aDate - bDate;
};

export function pickLongestStalled(
    rows: OnboardingRow[]
): LongestStalled | undefined {
    const eligible = rows.filter(
        (r) =>
            r.Status !== 'Live' &&
            r.Status !== 'Invite Sent' &&
            r.Priority !== 'Ignored'
    );
    if (eligible.length === 0) return undefined;

    const sorted = [...eligible].sort((a, b) => {
        if (b.Days !== a.Days) return b.Days - a.Days;
        // lowest priority first (Low > Medium → bigger upgrade payoff)
        const aRank = PRIORITY_RANK[a.Priority as ActionablePriority] ?? 99;
        const bRank = PRIORITY_RANK[b.Priority as ActionablePriority] ?? 99;
        if (aRank !== bRank) return aRank - bRank;
        const sd = compareStartDate(a, b);
        if (sd !== 0) return sd;
        return (a.CompanyName ?? '').localeCompare(b.CompanyName ?? '');
    });

    const top = sorted[0];
    const priority: ActionablePriority =
        top.Priority === 'High' || top.Priority === 'Medium' || top.Priority === 'Low'
            ? top.Priority
            : 'Low';

    return {
        id: top.Id,
        companyName: top.CompanyName,
        priority,
        days: top.Days,
    };
}

const STATUS_TO_PIPELINE_KEY: Partial<Record<Status, keyof PipelineCounts>> = {
    Live: 'live',
    Onboarding: 'onboarding',
    'Go Live Requested': 'goLiveRequested',
    'Form Received': 'formReceived',
    'Invite Sent': 'inviteSent',
};

export function computePipeline(rows: OnboardingRow[]): PipelineCounts {
    const counts: PipelineCounts = {
        live: 0,
        onboarding: 0,
        goLiveRequested: 0,
        formReceived: 0,
        inviteSent: 0,
        stalled: 0,
    };

    const stalledByStatus: Partial<Record<Status, number>> = {};

    for (const row of rows) {
        const key = STATUS_TO_PIPELINE_KEY[row.Status as Status];
        if (!key) continue; // unknown status — drop from bar
        counts[key] += 1;
        if (isStalled(row)) {
            counts.stalled += 1;
            stalledByStatus[row.Status as Status] =
                (stalledByStatus[row.Status as Status] ?? 0) + 1;
        }
    }

    // Subtract stalled rows from their source segments with a defensive clamp.
    for (const [status, stalledCount] of Object.entries(stalledByStatus)) {
        const key = STATUS_TO_PIPELINE_KEY[status as Status];
        if (!key) continue;
        counts[key] = Math.max(0, counts[key] - (stalledCount ?? 0));
    }

    return counts;
}

/**
 * Construct the per-scenario reasoning steps shown when the user expands the
 * "Show me the tasks you ran" toggle. Each line is annotated derived (from row
 * fields / tasks) or canned (constant string — placeholder for future wiring).
 */
export function buildReasoning(
    kind: ActionItem['kind'],
    row: OnboardingRow,
    tasks: PartnerTasks | null
): ReasoningStep[] {
    if (kind === 'scenario1') {
        const steps: ReasoningStep[] = [
            { label: 'Checked status', result: row.Status },
            { label: 'Days in pipeline', result: String(row.Days) },
        ];
        const incomplete = pickIncompleteTask(tasks);
        if (incomplete) {
            steps.push({ label: 'Incomplete task', result: incomplete });
        }
        const reminders = row.ReminderCount;
        if (typeof reminders === 'number') {
            steps.push({ label: 'Reminders sent', result: String(reminders) });
        }
        steps.push({
            label: 'Recommendation',
            result: `Email ${row.Email || 'the onboarding contact'} to follow up`,
        });
        return steps;
    }

    if (kind === 'scenario2') {
        const steps: ReasoningStep[] = [
            { label: 'Checked status', result: row.Status },
            { label: 'Tasks complete', result: 'All onboarding tasks complete' },
        ];
        if (typeof row.Days === 'number') {
            steps.push({ label: 'Days in pipeline', result: String(row.Days) });
        }
        steps.push({
            label: 'Recommendation',
            result: 'Push to production to start capturing orders',
        });
        return steps;
    }

    if (kind === 'scenario3') {
        return [
            { label: 'Checked status', result: 'Form Received' },
            {
                label: 'Form submitted',
                result:
                    row.Days === 0 || row.Days == null
                        ? 'today'
                        : `${row.Days} day${row.Days === 1 ? '' : 's'} ago`,
            },
            { label: 'Review state', result: 'Awaiting your review' },
            { label: 'Recommendation', result: 'Review the form to unblock onboarding' },
        ];
    }

    // Exhaustiveness guard: adding a 4th ActionItem kind would surface a TS error
    // here. Throwing also catches a hypothetical `'scenario4'` smuggled in from
    // outside the TS surface rather than returning the string-as-array.
    const _exhaustive: never = kind;
    throw new Error(`Unhandled ActionItem kind: ${String(_exhaustive)}`);
}

export type { ActionItem } from '../onboardingIntelligence.types';
